/**
 * P24 Return Page — shown after the user returns from P24 paywall.
 *
 * Reads order_id from query params and polls /api/v1/p24/order-status/
 * every 3 seconds until status becomes "paid" or "failed".
 *
 * If status stays "pending" for PENDING_AS_FAILED_MS we show "Płatność nieudana"
 * so behaviour is consistent when user chose payment failed in P24 (no callback is sent).
 *
 * Route: /platnosc/potwierdzenie/?order_id=123
 */

import { Box, CircularProgress, Typography } from '@material-ui/core';
import { parse } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { OrderStatus, useOrderStatus } from '@api/payment-p24.rest';
import { Button } from '@components/Button';
import { OfferHeader } from '@components/OfferHeader';
import { Spacer } from '@components/Spacer';
import { homeUrl } from '@urls';

import { theme } from '@styles';

const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
/** After this long with status "pending", we show "Płatność nieudana" so failed payments behave consistently */
const PENDING_AS_FAILED_MS = 45 * 1000;

export const P24ReturnPage: React.FC = () => {
  const { search } = useLocation();
  const { push } = useHistory();
  const { order_id } = parse(search);
  const orderId = order_id ? parseInt(order_id as string, 10) : null;

  const [timedOut, setTimedOut] = useState(false);
  /** After 45s of "pending" we show "Płatność nieudana" so behaviour is consistent when user chose fail in P24 */
  const [pendingTooLong, setPendingTooLong] = useState(false);

  const { data, isLoading, error } = useOrderStatus(orderId, {
    enabled: orderId !== null,
    stopPolling: pendingTooLong, // po 45s pending przestajemy odpytywać — zapobiega nieskończonemu pollingu
  });

  // Timeout after 5 minutes of polling
  useEffect(() => {
    if (!orderId) return;
    const timer = setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [orderId]);

  const status: OrderStatus | null = data?.status ?? null;

  // When status stays "pending" for PENDING_AS_FAILED_MS, treat as failed and stop polling
  useEffect(() => {
    if (status !== 'pending') {
      setPendingTooLong(false);
      return;
    }
    const t = setTimeout(() => setPendingTooLong(true), PENDING_AS_FAILED_MS);
    return () => clearTimeout(t);
  }, [status]);

  const displayFailed = status === 'failed' || (status === 'pending' && pendingTooLong);

  if (!orderId) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="h5">Brak numeru zamówienia</Typography>
        <Spacer height={20} />
        <Button
          variant="contained"
          color="primary"
          style={{ border: 'none' }}
          onClick={() => push(homeUrl)}
        >
          Strona główna
        </Button>
      </Box>
    );
  }

  return (
    <Box textAlign="center" py={6} maxWidth={600} mx="auto">
      <OfferHeader>Status płatności</OfferHeader>
      <Spacer height={20} />

      {/* Loading / Pending — show spinner only while truly waiting (max 45s then we show failed) */}
      {(isLoading || (status === 'pending' && !pendingTooLong)) && !timedOut && (
        <>
          <CircularProgress size={48} />
          <Spacer height={20} />
          <Typography>Przetwarzanie płatności...</Typography>
          <Typography variant="caption" color="textSecondary">
            Nie musisz czekać — jeżeli płatność będzie poprawna, Twoje ogłoszenie zostanie
            aktywowane automatycznie.
          </Typography>
        </>
      )}

      {/* Paid */}
      {status === 'paid' && (
        <>
          <Typography variant="h5" style={{ color: theme.palette.success.main }}>
            Opłacone
          </Typography>
          <Spacer height={10} />
          <Typography>
            Twoja płatność została potwierdzona. Ogłoszenie zostało opublikowane.
          </Typography>
        </>
      )}

      {/* Failed — from API or after 45s pending (so "payment failed" in P24 is always shown the same) */}
      {displayFailed && (
        <>
          <Typography variant="h5" style={{ color: theme.palette.error.main }}>
            Płatność nieudana
          </Typography>
          <Spacer height={10} />
          <Typography>
            Płatność nie powiodła się. Spróbuj ponownie lub skontaktuj się z nami.
          </Typography>
        </>
      )}

      {/* Timeout — only if still pending and we didn't already show "pending too long" as failed */}
      {timedOut && status === 'pending' && !pendingTooLong && (
        <>
          <Typography variant="h5" style={{ color: theme.palette.warning.main }}>
            Oczekiwanie na potwierdzenie
          </Typography>
          <Spacer height={10} />
          <Typography>
            Płatność nie została jeszcze potwierdzona. Jeśli dokonałeś płatności, poczekaj kilka
            minut — ogłoszenie zostanie aktywowane automatycznie. W razie problemów skontaktuj się
            z nami.
          </Typography>
        </>
      )}

      {/* Error */}
      {error && (
        <>
          <Typography variant="h5" style={{ color: theme.palette.error.main }}>
            Błąd
          </Typography>
          <Spacer height={10} />
          <Typography>Nie udało się sprawdzić statusu płatności.</Typography>
        </>
      )}

      <Spacer height={30} />
      <Button
        variant="contained"
        color="primary"
        style={{ border: 'none' }}
        onClick={() => push(homeUrl)}
      >
        Przejdź dalej
      </Button>
    </Box>
  );
};
